import React, {useEffect, useMemo, useRef, useState} from 'react'
import UiInputText from "../components/Form/UiInputText";
import UiInputCheckbox from "../components/Form/UiInputCheckbox";
import UiInputNumberInteger from "../components/Form/UiInputNumberInteger";
import Form from "../components/Form/Form";
import {useForm, FormProvider} from "react-hook-form";
import WindowBody from '../components/Window/WindowBody';
import WindowFooter from '../components/Window/WindowFooter';
import { UiButton } from '../components/UiButton';
import { get, post } from '../services/ApiService';
import { API_ENDPOINT } from '../components/Core/Grid/gridConstants';
import ContratoModeloEditor, { ContratoModeloEditorHandle, LARGURA_A4, MarcadorInfo } from '../components/ContratoModeloEditor';
import './ContratoModeloForm.scss';

interface GrupoMarcador { grupo: string; itens: MarcadorInfo[]; }

type Aba = 'editar' | 'final' | 'html';

const FORMATOS: { rotulo: React.ReactNode; titulo: string; comando: string; valor?: string }[] = [
    { rotulo: <b>B</b>, titulo: 'Negrito', comando: 'bold' },
    { rotulo: <i>I</i>, titulo: 'Itálico', comando: 'italic' },
    { rotulo: <u>S</u>, titulo: 'Sublinhado', comando: 'underline' },
    { rotulo: 'Título', titulo: 'Título do contrato', comando: 'formatBlock', valor: 'h1' },
    { rotulo: 'Cláusula', titulo: 'Título de cláusula', comando: 'formatBlock', valor: 'h2' },
    { rotulo: 'Parágrafo', titulo: 'Texto normal', comando: 'formatBlock', valor: 'p' },
    { rotulo: '• Lista', titulo: 'Lista com marcadores', comando: 'insertUnorderedList' },
    { rotulo: '1. Lista', titulo: 'Lista numerada', comando: 'insertOrderedList' },
    { rotulo: 'Centro', titulo: 'Centralizar', comando: 'justifyCenter' },
    { rotulo: 'Justificar', titulo: 'Justificar', comando: 'justifyFull' },
];

/**
 * Modelo do contrato, editado direto na página.
 *
 * Três abas sobre o mesmo corpo: **Editar**, o texto na página A4 com os marcadores
 * como etiquetas; **Documento final**, renderizado pelo servidor com o gerador de
 * verdade, cabeçalho e assinaturas; e **HTML**, para o ajuste fino.
 *
 * Modelo publicado é imutável, no mesmo princípio do template de checklist: salvar
 * mexendo no texto cria a versão seguinte, e os contratos já gerados continuam
 * apontando a versão que usaram.
 */
export default function ContratoModeloForm(props: any){
    const methods = useForm({
        defaultValues: props.data || { versao: 1, ativo: true }
    });
    const { handleSubmit, reset, watch, register, setValue, getValues } = methods;
    const [liberado, setLiberado] = useState(false);
    const [grupos, setGrupos] = useState<GrupoMarcador[]>([]);
    const [aba, setAba] = useState<Aba>('editar');
    const [previa, setPrevia] = useState('');
    const [carregandoPrevia, setCarregandoPrevia] = useState(false);
    const [escala, setEscala] = useState(1);
    const [moldura, setMoldura] = useState<{ cabecalho: string; rodape: string; estilos: string[]; quebra: string; assinaturas: string }>({ cabecalho: '', rodape: '', estilos: [], quebra: '<div class="quebra-pagina"></div>', assinaturas: '' });
    const [busca, setBusca] = useState('');
    const editorRef = useRef<ContratoModeloEditorHandle | null>(null);
    const textoRef = useRef<HTMLTextAreaElement | null>(null);
    const painelRef = useRef<HTMLDivElement | null>(null);

    const publicadoEm = watch('publicadoEm');
    const publicado = !!publicadoEm;
    const bloqueado = publicado && !liberado;
    const corpo: string = watch('corpo') || '';
    const id = watch('id');
    const nome = watch('nome');
    const versao = watch('versao');

    const marcadores = useMemo(() => grupos.flatMap((g) => g.itens), [grupos]);

    const gruposFiltrados = useMemo(() => {
        const termo = busca.trim().toLowerCase();
        if (!termo) return grupos;
        return grupos
            .map((g) => ({ ...g, itens: g.itens.filter((i) => `${i.rotulo} ${i.caminho}`.toLowerCase().includes(termo)) }))
            .filter((g) => g.itens.length > 0);
    }, [grupos, busca]);

    // Cabeçalho e rodapé de verdade no editor, com o nome e a versão que estão na tela.
    useEffect(() => {
        const timer = setTimeout(() => {
            post('/?route=uncontratomodelo/form&action=moldura', { dados: { id: id || '', nome: nome || '', versao: versao || 1 } })
                .then((resposta) => resposta.data?.success && setMoldura({ cabecalho: resposta.data.cabecalho, rodape: resposta.data.rodape, estilos: resposta.data.estilos || [], quebra: resposta.data.quebra, assinaturas: resposta.data.assinaturas || '' }))
                .catch(() => undefined);
        }, 600);
        return () => clearTimeout(timer);
    }, [id, nome, versao]);

    useEffect(() => {
        if (props.data) {
            reset(props.data);
            setLiberado(false);
        }
    }, [props.data, reset]);

    useEffect(() => {
        get('/?route=uncontratomodelo/form&action=marcadores')
            .then((resposta) => setGrupos(resposta.data?.grupos || []))
            .catch(() => setGrupos([]));
    }, []);

    // O documento final só é pedido com a aba aberta, e espera a digitação parar.
    useEffect(() => {
        if (aba !== 'final') return;
        const timer = setTimeout(() => {
            setCarregandoPrevia(true);
            post('/?route=uncontratomodelo/form&action=previa', { dados: { id: id || '', corpo } })
                .then((resposta) => setPrevia(resposta.data?.success ? resposta.data.html : `<p style="color:#b42318">${resposta.data?.message || 'Falha ao gerar a prévia.'}</p>`))
                .catch(() => setPrevia('<p style="color:#b42318">Falha ao gerar a prévia.</p>'))
                .finally(() => setCarregandoPrevia(false));
        }, 400);
        return () => clearTimeout(timer);
    }, [aba, corpo, id]);

    // A página A4 é reduzida para caber na largura do painel, sem rolagem lateral.
    useEffect(() => {
        const painel = painelRef.current;
        if (!painel) return;
        const medir = () => setEscala(Math.min(1, (painel.clientWidth - 48) / LARGURA_A4));
        medir();
        const observer = new ResizeObserver(medir);
        observer.observe(painel);
        return () => observer.disconnect();
    }, []);

    // Caminhos relativos do documento (print.css, logo) apontam para a API.
    const documentoFinal = previa
        ? previa.replace('<head>', `<head><base href="${API_ENDPOINT}/"/><style>html{zoom:${escala.toFixed(3)}}body{margin:0;padding:24px 0;background:#e9ecef}.print-me{display:none}.page{margin:16px auto;background:#fff;box-shadow:0 2px 12px rgba(0,0,0,.15)}</style>`)
        : '';

    const alterarCorpo = (html: string) => setValue('corpo', html, { shouldDirty: true });

    const inserirNoHtml = (caminho: string) => {
        const marcador = `{{${caminho}}}`;
        const campo = textoRef.current;
        const atual: string = getValues('corpo') || '';
        const inicio = campo ? campo.selectionStart : atual.length;
        const fim = campo ? campo.selectionEnd : atual.length;
        alterarCorpo(atual.slice(0, inicio) + marcador + atual.slice(fim));
        requestAnimationFrame(() => {
            if (!campo) return;
            campo.focus();
            campo.selectionStart = campo.selectionEnd = inicio + marcador.length;
        });
    };

    const inserirMarcador = (caminho: string) => {
        if (bloqueado) return;
        if (aba === 'html') {
            inserirNoHtml(caminho);
            return;
        }
        if (aba === 'final') setAba('editar');
        requestAnimationFrame(() => editorRef.current?.inserirMarcador(caminho));
    };

    const { ref: corpoRef, ...corpoRegister } = register('corpo');

    // Publicado vira versão nova no servidor; a tela manda o mesmo payload e o
    // backend decide, para as duas pontas não discordarem sobre quando versionar.
    const salvar = (dados: any) => props.triggerSubmit(dados);

    return (
        <FormProvider {...methods}>
            <Form onSubmit={handleSubmit(salvar)} methods={methods}>
                <WindowBody noPadding>
                    <div className="cmf">
                        <div className="cmf-previa" ref={painelRef}>
                            <div className="cmf-previa-barra">
                                <div className="cmf-abas">
                                    <button type="button" className={aba === 'editar' ? 'ativa' : ''} onClick={() => setAba('editar')}>Editar</button>
                                    <button type="button" className={aba === 'final' ? 'ativa' : ''} onClick={() => setAba('final')}>Documento final</button>
                                    <button type="button" className={aba === 'html' ? 'ativa' : ''} onClick={() => setAba('html')}>HTML</button>
                                </div>
                                {aba === 'editar' && (
                                    <div className="cmf-formatos">
                                        {FORMATOS.map((formato) => (
                                            <button
                                                key={formato.titulo}
                                                type="button"
                                                title={formato.titulo}
                                                disabled={bloqueado}
                                                onMouseDown={(evento) => evento.preventDefault()}
                                                onClick={() => editorRef.current?.comando(formato.comando, formato.valor)}
                                            >{formato.rotulo}</button>
                                        ))}
                                        <button
                                            type="button"
                                            className="cmf-quebra"
                                            title="Começa uma página nova a partir do cursor"
                                            disabled={bloqueado}
                                            onMouseDown={(evento) => evento.preventDefault()}
                                            onClick={() => editorRef.current?.quebrarPagina()}
                                        >Quebra de página</button>
                                    </div>
                                )}
                                {aba === 'final' && (
                                    <span className="cmf-previa-info">
                                        {carregandoPrevia ? 'atualizando…' : 'Com dados de exemplo, cabeçalho e assinaturas'}
                                    </span>
                                )}
                            </div>

                            {aba === 'editar' && (
                                <ContratoModeloEditor
                                    ref={editorRef}
                                    valor={corpo}
                                    onChange={alterarCorpo}
                                    marcadores={marcadores}
                                    desabilitado={bloqueado}
                                    base={API_ENDPOINT}
                                    escala={escala}
                                    cabecalho={moldura.cabecalho}
                                    rodape={moldura.rodape}
                                    estilos={moldura.estilos}
                                    quebra={moldura.quebra}
                                    assinaturas={moldura.assinaturas}
                                />
                            )}
                            {aba === 'final' && (
                                <iframe title="Documento final" srcDoc={documentoFinal} className="cmf-previa-iframe"/>
                            )}
                            {aba === 'html' && (
                                <textarea
                                    className="cmf-html"
                                    disabled={bloqueado}
                                    spellCheck={false}
                                    {...corpoRegister}
                                    ref={(elemento) => { corpoRef(elemento); textoRef.current = elemento; }}
                                />
                            )}
                        </div>

                        <div className="cmf-lateral">
                            {publicado && (
                                <div className="alert alert-warning cmf-alerta">
                                    <strong>Publicado{typeof publicadoEm === 'string' ? ` em ${publicadoEm}` : ''}.</strong>{' '}
                                    O texto está bloqueado porque contratos já gerados apontam para esta versão.
                                    Use <b>Editar como nova versão</b> para mudar.
                                </div>
                            )}

                            <div className="row">
                                <UiInputText size={12} name="nome" label="Nome do modelo"/>
                                <UiInputNumberInteger size={3} name="versao" label="Versão" readOnly/>
                                <UiInputText size={6} name="publicadoEm" label="Publicado em" readOnly/>
                                <UiInputCheckbox size={3} name="ativo" label="Ativo"/>
                            </div>

                            <div className="cmf-bloco">
                                <label className="form-label">Marcadores</label>
                                <input
                                    type="search"
                                    className="form-control form-control-sm cmf-busca"
                                    placeholder="Buscar marcador (ex.: cep, rg, pix)"
                                    value={busca}
                                    onChange={(evento) => setBusca(evento.target.value)}
                                />
                                {gruposFiltrados.length === 0 && <p className="cmf-ajuda">Nenhum marcador encontrado.</p>}
                                {gruposFiltrados.map((grupo) => (
                                    <div key={grupo.grupo} className="cmf-grupo">
                                        <div className="cmf-grupo-titulo">{grupo.grupo}</div>
                                        {grupo.itens.map((item) => (
                                            <button
                                                key={item.caminho}
                                                type="button"
                                                className="cmf-marcador"
                                                disabled={bloqueado}
                                                title={item.exemplo ? `Exemplo: ${item.exemplo}` : item.rotulo}
                                                onMouseDown={(evento) => evento.preventDefault()}
                                                onClick={() => inserirMarcador(item.caminho)}
                                            >
                                                <span className="cmf-marcador-rotulo">{item.rotulo}</span>
                                                <code>{`{{${item.caminho}}}`}</code>
                                            </button>
                                        ))}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                </WindowBody>

                <WindowFooter>
                    <UiButton variant="secondary" onClick={props.triggerClose}>Fechar</UiButton>
                    {bloqueado
                        ? <UiButton variant="warning" type="button" onClick={() => setLiberado(true)}>Editar como nova versão</UiButton>
                        : <UiButton variant="primary" type="submit">{publicado ? 'Salvar como nova versão' : 'Salvar'}</UiButton>
                    }
                </WindowFooter>
            </Form>
        </FormProvider>
    )
}
